import { useCallback, useState } from "react";

import { Params } from "react-router-dom";

import { DataType, FeatureType } from "@/jenesys-types/data/enums";
import { AppError, extractError } from "@/jenesys-types/errors";

import {
  PathParams,
  performDataAction,
  performFeatureAction
} from "@/services/api";

import useIsMounted from "./useIsMounted";

interface Options {
  enabled?: Boolean;
}

const useActionTrigger = <T = any>({
  actionName,
  pathParams = {},
  dataType,
  featureCluster,
  params,
  data = {},
  options,
  onSuccess,
  onError
}: {
  actionName: string;
  pathParams?: PathParams;
  dataType?: DataType;
  featureCluster?: FeatureType;
  params?: Params;
  data?: Record<string, any>;
  options?: Options;
  onSuccess?: (data: any) => void;
  onError?: (error: AppError) => void;
}) => {
  const isMounted = useIsMounted();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<AppError | undefined>();
  const [response, setResponse] = useState<T | undefined>();

  const { enabled = true } = options || {};

  const trigger = useCallback(
    ({
      pathParams: triggerPathParams,
      data: triggerData
    }: {
      pathParams?: PathParams;
      data?: Record<string, any>;
    } = {}) => {
      if ((!dataType && !featureCluster) || !enabled) {
        return;
      }

      setIsLoading(true);
      setError(undefined);

      const fulllPathParams = { ...pathParams, ...triggerPathParams };
      const fulllData = { ...data, ...triggerData };

      const responsePromise: Promise<T> = dataType
        ? performDataAction(
            dataType,
            actionName,
            fulllPathParams,
            params,
            fulllData
          )
        : performFeatureAction(
            featureCluster!,
            actionName,
            fulllPathParams,
            params,
            fulllData
          );
      responsePromise
        .then((res) => {
          if (isMounted()) {
            setResponse(res);
            onSuccess?.(res);
            setIsLoading(false);
          }
        })
        .catch((e) => {
          if (isMounted()) {
            setResponse(undefined);
            onError?.(extractError(e));
            setError(extractError(e));
            setIsLoading(false);
          }
        });
    },
    [
      dataType,
      featureCluster,
      enabled,
      actionName,
      pathParams,
      params,
      data,
      isMounted,
      onSuccess,
      onError
    ]
  );

  return {
    isLoading,
    error,
    response,
    trigger
  };
};

export default useActionTrigger;
